import { useMutation, useQuery } from '@tanstack/react-query';

import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants/networking.constants';

const { get, post } = erpApi();

// ================================================================
// Tipos
// ================================================================

export type DosFactorMetodo = 'email' | 'app';

export interface DosFactorEstado {
  habilitado: boolean;
  metodo: DosFactorMetodo | null;
  tiene_backup_codes: boolean;
  email: string | null;
  es_usuario_integracion: boolean;
  mfa_obligatorio?: boolean;
  mfa_setup_pendiente?: boolean;
  mfa_grace_until?: string | null;
}

export interface DosFactorSetupAppResp {
  otpauth_uri: string;
  secreto: string;
}

export interface DosFactorSetupEmailResp {
  email_mascarado: string;
}

export interface DosFactorActivarResp {
  backup_codes: string[];
}

// ================================================================
// Endpoints (autenticados)
// ================================================================

const url = (p: string) => `${urlBackend}api/v1/dos-factor/${p}`;

export const fetchDosFactorEstado = async () => {
  const res = await get<DosFactorEstado>(url('estado/'), true);
  return res.data;
};

export const setupDosFactorApp = async () => {
  const res = await post<DosFactorSetupAppResp>(url('setup-app/'), {}, true);
  return res.data;
};

export const confirmarDosFactorApp = async (codigo: string) => {
  const res = await post<DosFactorActivarResp>(
    url('confirmar-app/'),
    { codigo },
    true
  );
  return res.data;
};

export const setupDosFactorEmail = async () => {
  const res = await post<DosFactorSetupEmailResp>(url('setup-email/'), {}, true);
  return res.data;
};

export const confirmarDosFactorEmail = async (codigo: string) => {
  const res = await post<DosFactorActivarResp>(
    url('confirmar-email/'),
    { codigo },
    true
  );
  return res.data;
};

export interface DesactivarDosFactorPayload {
  password: string;
  codigo?: string;
  usa_backup?: boolean;
}

export const desactivarDosFactor = async (payload: DesactivarDosFactorPayload) => {
  const res = await post(url('desactivar/'), payload, true);
  return res;
};

export const regenerarBackupCodes = async (password: string) => {
  const res = await post<DosFactorActivarResp>(
    url('regenerar-backup/'),
    { password },
    true
  );
  return res.data;
};

// ================================================================
// Endpoints (login paso 2, sin auth)
// ================================================================

export interface DosFactorVerificarPayload {
  challenge_token: string;
  codigo: string;
  usa_backup?: boolean;
  forzar?: boolean;
}

export const reenviarCodigoDosFactor = async (challenge_token: string) => {
  const res = await post<{ email_mascarado: string }>(
    url('enviar-codigo/'),
    { challenge_token },
    false
  );
  return res.data;
};

export const verificarDosFactor = async (payload: DosFactorVerificarPayload) => {
  const res = await post(url('verificar/'), payload, false);
  return res;
};

// ================================================================
// React Query hooks
// ================================================================

export const useDosFactorEstado = (enabled = true) =>
  useQuery({
    queryKey: ['dos-factor-estado'],
    queryFn: fetchDosFactorEstado,
    enabled,
    staleTime: 0,
  });

export const useSetupDosFactorApp = () =>
  useMutation({ mutationFn: setupDosFactorApp });

export const useConfirmarDosFactorApp = () =>
  useMutation({ mutationFn: (codigo: string) => confirmarDosFactorApp(codigo) });

export const useSetupDosFactorEmail = () =>
  useMutation({ mutationFn: setupDosFactorEmail });

export const useConfirmarDosFactorEmail = () =>
  useMutation({ mutationFn: (codigo: string) => confirmarDosFactorEmail(codigo) });

export const useDesactivarDosFactor = () =>
  useMutation({
    mutationFn: (payload: DesactivarDosFactorPayload) =>
      desactivarDosFactor(payload),
  });

export const useRegenerarBackupCodes = () =>
  useMutation({ mutationFn: (password: string) => regenerarBackupCodes(password) });
